import {CalendarPlus, Clock, ExternalLink, MapPin} from 'lucide-react';
import {useIntl} from 'react-intl';
import type {DetectedMeeting} from '../../../../store/features/calls/callsService';
import {showToast} from '../../../../store/features/Notifications/notificationsSlice';
import {useAppDispatch} from '../../../../store/store';
import {insightsMessages as m} from '../insightsMessages';
import {lucide, secondaryButton, tintButton} from '../insightsUi';
import {logCalendarExported} from '../insightsLog';
import {downloadText} from '../transcript/exportTranscript';
import {buildIcs, meetingUid} from '../calendar/ics';
import {googleCalendarLink} from '../calendar/googleCalendarLink';
import {DEFAULT_MEETING_MINUTES, formatMeetingDate, formatMeetingTime, meetingStart} from '../calendar/meetingTime';
import {Citations} from './CitationChip';

interface MeetingCardProps {
    meeting: DetectedMeeting;
    index: number;
    callId: number;
    callStartedAt?: string | null;
}

export const MeetingCard = ({meeting, index, callId, callStartedAt}: MeetingCardProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const start = meetingStart(meeting);
    const minutes = meeting.durationMinutes ?? DEFAULT_MEETING_MINUTES;
    const date = formatMeetingDate(start, meeting.timeZone);
    const time = formatMeetingTime(start, meeting.timeZone);
    const callDate = callStartedAt ? formatMeetingDate({kind: 'datetime', utc: new Date(callStartedAt)}) : null;
    const description = callDate ? intl.formatMessage(m.calendarDescription, {date: callDate}) : '';

    const downloadIcs = () => {
        const ics = buildIcs({
            uid: meetingUid(callId, index),
            title: meeting.title,
            start,
            durationMinutes: minutes,
            location: meeting.location,
            description,
            stamp: new Date(),
        });
        downloadText(`call-${callId}-meeting-${index + 1}.ics`, ics, 'text/calendar;charset=utf-8');
        dispatch(showToast({title: intl.formatMessage(m.icsDownloaded), description: '', type: 'success'}));
        logCalendarExported('ics');
    };

    const googleLink = googleCalendarLink({title: meeting.title, start, durationMinutes: minutes, location: meeting.location, details: description});

    return (
        <li className="flex flex-col gap-3 rounded-card border border-solid border-line p-4" data-testid="insights-meeting">
            <div className="flex flex-col gap-1">
                <span className="text-callout font-semibold text-ink">
                    {meeting.title}
                    <Citations citations={meeting.citations}/>
                </span>
                <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-footnote tabular-nums text-ink-muted">
                    <span className="inline-flex items-center gap-1">
                        <Clock size={13} aria-hidden="true" className={lucide}/>
                        {date} · {time ?? intl.formatMessage(m.meetingAllDay)}
                        {time && meeting.durationMinutes ? ` · ${intl.formatMessage(m.meetingDuration, {minutes: meeting.durationMinutes})}` : ''}
                    </span>
                    {meeting.location && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={13} aria-hidden="true" className={lucide}/>
                            {meeting.location}
                        </span>
                    )}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={downloadIcs} className={tintButton}>
                    <CalendarPlus size={14} aria-hidden="true" className={lucide}/>
                    {intl.formatMessage(m.downloadIcs)}
                    <span className="font-normal text-ink-muted-strong">{intl.formatMessage(m.downloadIcsApps)}</span>
                </button>
                <a
                    href={googleLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => logCalendarExported('google')}
                    className={secondaryButton}
                >
                    {intl.formatMessage(m.googleCalendar)}
                    <ExternalLink size={13} aria-hidden="true" className={lucide}/>
                </a>
            </div>
        </li>
    );
};
